import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CopyableSnippet } from './CopyableSnippet';
import { McpCategoryRules } from './McpCategoryRules';
import {
  MCP_ENDPOINT_URL,
  MCP_SERVER_NAME,
  MCP_TRANSPORT,
  MCP_TOOL_NAME,
  MCP_TOKEN_LIFETIME_DAYS,
  buildClaudeMcpAddCommand,
  buildMcpJsonSnippet,
} from '../mcpSetup';

/**
 * AOZONE-104: the persistent MCP setup instructions on /user/tokens — the first
 * end-user MCP documentation in the repo, written for someone starting from
 * nothing. Everything here renders without minting a token (acceptance criterion
 * 2), so the `/mbr link` prerequisite and the per-category field rules can be
 * read before committing to a token you only see once.
 *
 * The config shown is the one verified against prod from a real Claude Code
 * client; it is built by `../mcpSetup` so the reveal panel's copy-ready command
 * and this section cannot drift.
 */

const CODE = 'rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]';

/**
 * Two server-side strings are quoted verbatim below rather than imported:
 * `No active Slack identity link for <email>` (apps/mcp-server/src/createEntry.ts)
 * and the beta reply `BETA_BLOCK_MESSAGE` (apps/slack-bot/src/commandRouter.ts).
 * Importing either would pull a Lambda package's dependency tree toward this
 * browser bundle — the thing the `categoryFieldRules` leaf-ness lock exists to
 * prevent — so these are copies: they can drift from the originals and no test
 * detects it. Accepted cost (AOZONE-104); reword them together.
 */

export function McpSetupInstructions() {
  return (
    <Card className="mt-8">
      <CardHeader>
        {/* A real heading element, not CardTitle's div: this section sits under
            the page h1 and owns the h3 step headings below. */}
        <h2 className="font-semibold leading-none tracking-tight">
          Use OZONE from Claude Code (MCP)
        </h2>
        <CardDescription>
          Connect an MCP client to OZONE and create MBR entries with{' '}
          <code className={CODE}>{MCP_TOOL_NAME}</code> instead of the Slack modal.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 text-sm text-slate-700">
        <div
          data-testid="mcp-link-prerequisite"
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
        >
          <div className="font-medium">
            First, link your Slack identity — run <code className={CODE}>/mbr link</code> in Slack.
          </div>
          <div className="mt-1 text-xs text-amber-800">
            Entries are attributed through that link, so without it every{' '}
            <code className={CODE}>{MCP_TOOL_NAME}</code> call fails with{' '}
            <code className={CODE}>No active Slack identity link for &lt;your email&gt;</code>. Do
            this once, before you generate a token.
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-800">
            <li>
              Your Slack alias must be enabled for the MBR beta. If it isn&rsquo;t,{' '}
              <code className={CODE}>/mbr link</code> replies only{' '}
              <em>&ldquo;MBR capture is in beta. Ping @dmotles if you&rsquo;d like access.&rdquo;</em>{' '}
              — that reply is the gate, not an error.
            </li>
            <li>
              When the link flow sends you to Google, pick the Google account whose email matches
              your Slack profile email. Any account can sign in, but a mismatch is rejected after
              sign-in.
            </li>
          </ul>
        </div>

        <div className="space-y-2">
          <h3 className="font-medium text-slate-900">1. Generate a token</h3>
          <p>
            Use the <strong>Generate MCP token</strong> button above. The plaintext is shown once,
            with a ready-to-run command that already has the token in it — the fastest path is to
            copy that and skip the rest of this section.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="font-medium text-slate-900">2. Register the server</h3>
          <p data-testid="mcp-connection-facts">
            OZONE speaks MCP over Streamable HTTP at{' '}
            <code className={CODE}>{MCP_ENDPOINT_URL}</code>, so the transport is{' '}
            <code className={CODE}>{MCP_TRANSPORT}</code> (not stdio). The token is not a URL
            parameter: it goes in an <code className={CODE}>Authorization: Bearer</code> request
            header. Register it under the server name{' '}
            <code className={CODE}>{MCP_SERVER_NAME}</code>:
          </p>
          <CopyableSnippet
            text={buildClaudeMcpAddCommand()}
            copyLabel="Copy command template"
            testId="mcp-add-command"
          />
          <p className="text-xs text-slate-500">
            Replace <code className={CODE}>&lt;TOKEN&gt;</code> with your token.{' '}
            <code className={CODE}>--scope user</code> makes the server available in all your
            projects. Needs Claude Code installed and new enough to support{' '}
            <code className={CODE}>--transport http</code>; note the token lands in your shell
            history, so use the JSON form below if you would rather it didn&rsquo;t.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="font-medium text-slate-900">2b. Or configure it by hand</h3>
          <p>
            Clients that read a JSON config (<code className={CODE}>.mcp.json</code> and
            equivalents) want the same three facts:
          </p>
          <CopyableSnippet
            text={buildMcpJsonSnippet()}
            copyLabel="Copy JSON"
            testId="mcp-json-config"
          />
          <p className="text-xs text-slate-500">
            The token is a credential — keep it in your user-level config and never commit a{' '}
            <code className={CODE}>.mcp.json</code> containing one.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="font-medium text-slate-900">3. Verify</h3>
          <p data-testid="mcp-verify">
            Run <code className={CODE}>/mcp</code> in Claude Code. You should see{' '}
            <code className={CODE}>{MCP_SERVER_NAME}</code> listed as connected, exposing one tool:{' '}
            <code className={CODE}>{MCP_TOOL_NAME}</code>. If it is not connected, the token is
            usually expired or was pasted with the <code className={CODE}>Bearer </code> prefix
            duplicated.
          </p>
        </div>

        <div className="space-y-2" data-testid="mcp-token-lifetime">
          <h3 className="font-medium text-slate-900">Token lifetime</h3>
          <p>
            Tokens are static bearer tokens — there is no refresh, and a token cannot be extended.
            They expire {MCP_TOKEN_LIFETIME_DAYS} days after minting by default (the table above
            shows each token&rsquo;s expiry). On expiry, re-mint here and re-run the command above
            with the new token.
          </p>
        </div>

        <Separator />

        <div className="space-y-2">
          <h3 className="font-medium text-slate-900">
            Which fields <code className={CODE}>{MCP_TOOL_NAME}</code> takes
          </h3>
          <McpCategoryRules />
        </div>
      </CardContent>
    </Card>
  );
}
